import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { formatDate, ROL_ETIKETLERI } from "../lib/format";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import { Skeleton } from "./ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./ui/table";
import {
  UserPlus,
  MoreVertical,
  UserCog,
  Power,
  Trash2,
  KeyRound,
  Loader2,
} from "lucide-react";

export const ROL_BADGE_STYLE = {
  yonetici: "bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--primary))]",
  operasyon: "bg-[hsl(var(--accent))]/20 text-[hsl(43_60%_28%)] border border-[hsl(var(--accent))]/40 hover:bg-[hsl(var(--accent))]/20",
  goruntuleyici: "bg-secondary text-secondary-foreground hover:bg-secondary",
};

export const RolBadge = ({ role, testid }) => (
  <Badge className={ROL_BADGE_STYLE[role] || ""} data-testid={testid}>
    {ROL_ETIKETLERI[role] || role}
  </Badge>
);

const EMPTY_FORM = { email: "", ad_soyad: "", password: "", role: "operasyon" };

export function UserManagement({ currentUser }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [roleDialog, setRoleDialog] = useState(null); // {user, newRole}
  const [passwordDialog, setPasswordDialog] = useState(null); // {user, newPassword}
  const [deleteTarget, setDeleteTarget] = useState(null);

  const fetchUsers = useCallback(async () => {
    try {
      const res = await api.get("/users");
      setUsers(res.data);
    } catch {
      toast.error("Kullanıcılar yüklenemedi.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleCreate = async () => {
    if (!form.email || !form.password) {
      toast.error("E-posta ve şifre zorunludur.");
      return;
    }
    if (form.password.length < 6) {
      toast.error("Şifre en az 6 karakter olmalıdır.");
      return;
    }
    setSaving(true);
    try {
      await api.post("/users", form);
      toast.success("Kullanıcı oluşturuldu.");
      setCreateOpen(false);
      setForm(EMPTY_FORM);
      fetchUsers();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Kullanıcı oluşturulamadı.");
    } finally {
      setSaving(false);
    }
  };

  const handleRoleChange = async () => {
    if (!roleDialog) return;
    setSaving(true);
    try {
      await api.patch(`/users/${roleDialog.user.id}`, { role: roleDialog.newRole });
      toast.success("Rol güncellendi.");
      setRoleDialog(null);
      fetchUsers();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Rol güncellenemedi.");
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (u) => {
    try {
      await api.patch(`/users/${u.id}`, { is_active: !u.is_active });
      toast.success(u.is_active ? "Kullanıcı pasif yapıldı." : "Kullanıcı aktif yapıldı.");
      fetchUsers();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "İşlem yapılamadı.");
    }
  };

  const handlePasswordReset = async () => {
    if (!passwordDialog?.newPassword || passwordDialog.newPassword.length < 6) {
      toast.error("Şifre en az 6 karakter olmalıdır.");
      return;
    }
    setSaving(true);
    try {
      await api.patch(`/users/${passwordDialog.user.id}`, {
        password: passwordDialog.newPassword,
      });
      toast.success("Şifre güncellendi.");
      setPasswordDialog(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Şifre güncellenemedi.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/users/${deleteTarget.id}`);
      toast.success("Kullanıcı silindi.");
      setDeleteTarget(null);
      fetchUsers();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Kullanıcı silinemedi.");
    }
  };

  const RowActions = ({ u }) => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="min-h-[44px] min-w-[44px]"
          data-testid={`users-row-${u.id}-actions-button`}
          aria-label="İşlemler"
        >
          <MoreVertical className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="bg-card w-48">
        <DropdownMenuItem
          onClick={() => setRoleDialog({ user: u, newRole: u.role })}
          data-testid="users-row-change-role-action"
          className="cursor-pointer min-h-[44px]"
        >
          <UserCog className="mr-2 h-4 w-4" />
          Rol Değiştir
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => setPasswordDialog({ user: u, newPassword: "" })}
          data-testid="users-row-reset-password-action"
          className="cursor-pointer min-h-[44px]"
        >
          <KeyRound className="mr-2 h-4 w-4" />
          Şifre Sıfırla
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => handleToggleActive(u)}
          disabled={u.id === currentUser.id}
          data-testid="users-row-toggle-active-action"
          className="cursor-pointer min-h-[44px]"
        >
          <Power className="mr-2 h-4 w-4" />
          {u.is_active ? "Pasif Yap" : "Aktif Yap"}
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => setDeleteTarget(u)}
          disabled={u.id === currentUser.id}
          data-testid="users-row-delete-action"
          className="cursor-pointer min-h-[44px] text-destructive focus:text-destructive"
        >
          <Trash2 className="mr-2 h-4 w-4" />
          Sil
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  return (
    <div className="rounded-xl border bg-card p-5 sm:p-6 shadow-[0_1px_0_rgba(14,27,46,0.06),0_10px_30px_rgba(14,27,46,0.06)]">
      <div className="flex flex-wrap items-center gap-3 mb-1">
        <div>
          <h2 className="font-display text-lg font-semibold">Kullanıcı Yönetimi</h2>
          <p className="text-sm text-muted-foreground">
            Kullanıcıları oluşturun, rollerini yönetin ve erişimi kontrol edin.
          </p>
        </div>
        <Button
          onClick={() => setCreateOpen(true)}
          className="ml-auto gap-2 min-h-[44px] transition-colors duration-150 active:scale-[0.98]"
          data-testid="users-create-open-button"
        >
          <UserPlus className="h-4 w-4" />
          Yeni Kullanıcı
        </Button>
      </div>

      {loading ? (
        <div className="mt-4 space-y-2">
          {[...Array(4)].map((_, i) => (
            <Skeleton key={i} className="h-14 w-full" />
          ))}
        </div>
      ) : (
        <>
          {/* Desktop table */}
          <div className="hidden md:block mt-4 rounded-lg border overflow-hidden">
            <Table data-testid="users-table">
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead>E-posta</TableHead>
                  <TableHead>Ad Soyad</TableHead>
                  <TableHead>Rol</TableHead>
                  <TableHead>Durum</TableHead>
                  <TableHead>Oluşturma</TableHead>
                  <TableHead className="w-14 text-right">İşlemler</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((u) => (
                  <TableRow key={u.id} data-testid="users-table-row">
                    <TableCell className="font-medium">{u.email}</TableCell>
                    <TableCell>{u.ad_soyad || "—"}</TableCell>
                    <TableCell>
                      <RolBadge role={u.role} testid={`users-row-${u.id}-role-badge`} />
                    </TableCell>
                    <TableCell>
                      {u.is_active ? (
                        <Badge className="bg-emerald-50 text-emerald-800 border border-emerald-200 hover:bg-emerald-50">
                          Aktif
                        </Badge>
                      ) : (
                        <Badge className="bg-slate-100 text-slate-600 border border-slate-200 hover:bg-slate-100">
                          Pasif
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-muted-foreground text-sm">
                      {formatDate(u.created_at)}
                    </TableCell>
                    <TableCell className="text-right">
                      <RowActions u={u} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {/* Mobile card list */}
          <div className="md:hidden mt-4 space-y-3" data-testid="users-mobile-list">
            {users.map((u) => (
              <div key={u.id} className="rounded-lg border p-4">
                <div className="flex items-start gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium">{u.email}</p>
                    <p className="text-xs text-muted-foreground">{u.ad_soyad || "—"}</p>
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                      <RolBadge role={u.role} />
                      {u.is_active ? (
                        <Badge className="bg-emerald-50 text-emerald-800 border border-emerald-200 hover:bg-emerald-50">
                          Aktif
                        </Badge>
                      ) : (
                        <Badge className="bg-slate-100 text-slate-600 border border-slate-200 hover:bg-slate-100">
                          Pasif
                        </Badge>
                      )}
                    </div>
                  </div>
                  <RowActions u={u} />
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Create user dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="bg-card sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display">Yeni Kullanıcı</DialogTitle>
            <DialogDescription>
              Yeni bir kullanıcı oluşturun ve rol atayın.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="new-email">E-posta *</Label>
              <Input
                id="new-email"
                type="email"
                placeholder="ornek@3afinans.com"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="h-12"
                data-testid="users-create-email-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="new-name">Ad Soyad</Label>
              <Input
                id="new-name"
                placeholder="Ad Soyad"
                value={form.ad_soyad}
                onChange={(e) => setForm({ ...form, ad_soyad: e.target.value })}
                className="h-12"
                data-testid="users-create-name-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="new-password">Şifre * (en az 6 karakter)</Label>
              <Input
                id="new-password"
                type="password"
                placeholder="••••••••"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="h-12"
                data-testid="users-create-password-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Rol *</Label>
              <Select
                value={form.role}
                onValueChange={(v) => setForm({ ...form, role: v })}
              >
                <SelectTrigger className="h-12" data-testid="users-create-role-select">
                  <SelectValue placeholder="Rol seçin" />
                </SelectTrigger>
                <SelectContent className="bg-card">
                  <SelectItem value="yonetici">Yönetici</SelectItem>
                  <SelectItem value="operasyon">Operasyon</SelectItem>
                  <SelectItem value="goruntuleyici">Görüntüleyici</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setCreateOpen(false)}
              className="min-h-[44px]"
              data-testid="users-create-cancel-button"
            >
              Vazgeç
            </Button>
            <Button
              onClick={handleCreate}
              disabled={saving}
              className="min-h-[44px]"
              data-testid="users-create-submit-button"
            >
              {saving ? "Kaydediliyor…" : "Kaydet"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Role change dialog */}
      <Dialog open={!!roleDialog} onOpenChange={(o) => !o && setRoleDialog(null)}>
        <DialogContent className="bg-card sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-display">Rol Değiştir</DialogTitle>
            <DialogDescription>{roleDialog?.user?.email}</DialogDescription>
          </DialogHeader>
          <Select
            value={roleDialog?.newRole}
            onValueChange={(v) => setRoleDialog((d) => ({ ...d, newRole: v }))}
          >
            <SelectTrigger className="h-12" data-testid="users-role-change-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-card">
              <SelectItem value="yonetici">Yönetici</SelectItem>
              <SelectItem value="operasyon">Operasyon</SelectItem>
              <SelectItem value="goruntuleyici">Görüntüleyici</SelectItem>
            </SelectContent>
          </Select>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRoleDialog(null)} className="min-h-[44px]">
              Vazgeç
            </Button>
            <Button
              onClick={handleRoleChange}
              disabled={saving}
              className="min-h-[44px]"
              data-testid="users-role-change-submit-button"
            >
              {saving ? "Güncelleniyor…" : "Güncelle"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Password reset dialog */}
      <Dialog open={!!passwordDialog} onOpenChange={(o) => !o && setPasswordDialog(null)}>
        <DialogContent className="bg-card sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-display">Şifre Sıfırla</DialogTitle>
            <DialogDescription>{passwordDialog?.user?.email}</DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label htmlFor="reset-password">Yeni Şifre (en az 6 karakter)</Label>
            <Input
              id="reset-password"
              type="password"
              value={passwordDialog?.newPassword || ""}
              onChange={(e) =>
                setPasswordDialog((d) => ({ ...d, newPassword: e.target.value }))
              }
              className="h-12"
              data-testid="users-password-reset-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPasswordDialog(null)} className="min-h-[44px]">
              Vazgeç
            </Button>
            <Button
              onClick={handlePasswordReset}
              disabled={saving}
              className="min-h-[44px]"
              data-testid="users-password-reset-submit-button"
            >
              {saving ? "Kaydediliyor…" : "Kaydet"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent className="bg-card">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display">Kullanıcıyı Sil</AlertDialogTitle>
            <AlertDialogDescription>
              <strong>{deleteTarget?.email}</strong> kullanıcısı kalıcı olarak silinecek.
              Bu işlem geri alınamaz. Devam etmek istiyor musunuz?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="min-h-[44px]" data-testid="users-delete-cancel-button">
              Vazgeç
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="min-h-[44px] bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="users-delete-confirm-button"
            >
              Sil
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
